import i18next from 'i18next';
import { httpClient, type HttpMethod, type HttpRequestOptions, HttpError } from '../../../shared/api/httpClient';
import type { TokenResponse } from '../../../shared/api/contracts/styleTransferSchemas';
import { requestDeviceToken } from '../../style-transfer/api/requestDeviceToken';
import { useSessionStore } from '../../style-transfer/state/sessionStore';
import { useAuthStore } from '../state/authStore';
import { showToast } from '../../../shared/notifications/toastStore';

type HeadersInitLike = HeadersInit | undefined;

interface UnauthorizedResolutionRetry {
  type: 'retry';
  token: TokenResponse;
}

interface UnauthorizedResolutionFail {
  type: 'fail';
}

type UnauthorizedResolution = UnauthorizedResolutionRetry | UnauthorizedResolutionFail;

const AUTHORIZATION_HEADER = 'Authorization';
const DEVICE_TOKEN_HEADER = 'X-API-Token';
const TOAST_COOLDOWN_MS = 4000;

const toastCooldown = new Map<string, number>();
let deviceRefreshPromise: Promise<TokenResponse> | null = null;

export interface AuthenticatedRequestOptions<TBody = unknown> extends HttpRequestOptions<TBody> {
  /**
   * Overrides the token used for this request. Defaults to the latest value in session store.
   */
  authToken?: TokenResponse | null;
  /** Optional label used for diagnostics. */
  retryLabel?: string;
}

export interface FetchWithAuthRetryOptions {
  execute: (authHeaders: Record<string, string>) => Promise<Response>;
  authToken?: TokenResponse | null;
  retryLabel?: string;
}

function normalizeHeaders(init: HeadersInitLike): Record<string, string> {
  if (!init) {
    return {};
  }

  if (init instanceof Headers) {
    return Array.from(init.entries()).reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }

  if (Array.isArray(init)) {
    return init.reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }

  return { ...init };
}

function stripAuthHeaders(headers: Record<string, string>): Record<string, string> {
  const sanitized = { ...headers };
  delete sanitized[AUTHORIZATION_HEADER];
  delete sanitized[DEVICE_TOKEN_HEADER];
  return sanitized;
}

function buildAuthHeaders(token: TokenResponse | null): Record<string, string> {
  if (!token) {
    return {};
  }

  if (token.tokenType === 'user') {
    return {
      [AUTHORIZATION_HEADER]: `Bearer ${token.token}`
    };
  }

  return {
    [DEVICE_TOKEN_HEADER]: token.token
  };
}

function mergeAuthHeaders(
  headers: HeadersInitLike,
  token: TokenResponse | null
): Record<string, string> {
  const baseHeaders = stripAuthHeaders(normalizeHeaders(headers));
  const authHeaders = buildAuthHeaders(token);
  return {
    ...baseHeaders,
    ...authHeaders
  };
}

function emitToast(key: string, tone: 'info' | 'warning' | 'danger') {
  const now = Date.now();
  const last = toastCooldown.get(key) ?? 0;
  if (now - last < TOAST_COOLDOWN_MS) {
    return;
  }
  toastCooldown.set(key, now);
  const message = i18next.t(key);
  if (typeof message === 'string' && message.length > 0) {
    showToast({ message, tone });
  }
}

async function refreshDeviceTokenOnce(): Promise<TokenResponse> {
  if (!deviceRefreshPromise) {
    deviceRefreshPromise = (async () => {
      const refreshed = await requestDeviceToken();
      return refreshed;
    })().finally(() => {
      deviceRefreshPromise = null;
    });
  }
  return deviceRefreshPromise;
}

async function resolveUnauthorized(retryLabel?: string): Promise<UnauthorizedResolution> {
  const session = useSessionStore.getState();
  const auth = useAuthStore.getState();
  const activeToken = session.token;

  if (!activeToken) {
    emitToast('app.notifications.sessionExpired', 'warning');
    return { type: 'fail' };
  }

  if (activeToken.tokenType === 'user') {
    emitToast('app.notifications.sessionExpired', 'warning');
    auth.signOut();
    return { type: 'fail' };
  }

  try {
    const refreshed = await refreshDeviceTokenOnce();
    session.setToken(refreshed);
    emitToast('app.notifications.deviceTokenRefreshed', 'info');
    return {
      type: 'retry',
      token: refreshed
    };
  } catch (error) {
    if (retryLabel) {
      console.warn('[auth] failed to refresh device token', { retryLabel, error });
    } else {
      console.warn('[auth] failed to refresh device token', error);
    }
    session.clearSession();
    emitToast('app.notifications.deviceTokenRefreshFailed', 'warning');
    return { type: 'fail' };
  }
}

export async function requestWithAuthRetry<TResponse, TBody = unknown>(
  method: HttpMethod,
  path: string,
  options: AuthenticatedRequestOptions<TBody> = {}
): Promise<TResponse> {
  const { authToken = useSessionStore.getState().token, retryLabel, ...rest } = options;
  const preparedOptions: HttpRequestOptions<TBody> = {
    ...rest,
    headers: mergeAuthHeaders(rest.headers, authToken)
  };

  try {
    return await httpClient.request<TResponse, TBody>(method, path, preparedOptions);
  } catch (error) {
    if (error instanceof HttpError && error.status === 401) {
      const resolution = await resolveUnauthorized(retryLabel);
      if (resolution.type === 'retry') {
        const retriedOptions: HttpRequestOptions<TBody> = {
          ...rest,
          headers: mergeAuthHeaders(rest.headers, resolution.token)
        };
        return httpClient.request<TResponse, TBody>(method, path, retriedOptions);
      }
    }
    throw error;
  }
}

export async function fetchWithAuthRetry({
  execute,
  authToken = useSessionStore.getState().token,
  retryLabel
}: FetchWithAuthRetryOptions): Promise<Response> {
  let currentHeaders = mergeAuthHeaders(undefined, authToken);

  const attempt = () => execute(currentHeaders);

  let response = await attempt();

  if (response.status !== 401) {
    return response;
  }

  const resolution = await resolveUnauthorized(retryLabel);
  if (resolution.type !== 'retry') {
    return response;
  }

  currentHeaders = mergeAuthHeaders(undefined, resolution.token);
  response = await attempt();
  return response;
}
